import { yupResolver } from '@hookform/resolvers/yup';
import { LoadingButton } from '@mui/lab';
import {
  Stack,
  Dialog,
  Button,
  MenuItem,
  Typography,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { useForm } from 'react-hook-form';
import FormProvider, { RHFSelect, RHFUploadBox } from 'src/components/hook-form';
import { PositionTxType, UserTransactionPaymentType } from 'src/type/position';
import axios, { endpoints } from 'src/utils/axios';
import { toastError } from 'src/utils/format-error';
import * as Yup from 'yup';
import { useCallback } from 'react';
import { fData, fNumber } from 'src/utils/format-number';
import { uploadImage } from 'src/libs/firebase/firebase-upload';
import { useBoolean } from 'src/hooks/use-boolean';
import useCurrentDateTime from 'src/hooks/use-current-date-time';
import { paymentTypeLabel } from './utils';

const paymentTypeOptions = [
  {
    label: 'โอนเงิน',
    value: 'BANK',
  },
  {
    label: 'เงินสด',
    value: 'CASH',
  },
  {
    label: 'เครดิต',
    value: 'CREDIT',
  },
];

type Props = {
  open: boolean;
  onClose: () => void;
  tx: PositionTxType;
  handleReload: () => void;
};

const defaultValues = {
  paymentImage: null as unknown as File,
  paymentType: '',
};

export default function PositionUpdatePaymentDialog({ open, onClose, tx, handleReload }: Props) {
  const currentDateTime = useCurrentDateTime();
  const isConfirmingCredit = useBoolean(false);
  const isPaymentTypeNull = !tx.paymentType;
  const isBank = tx.paymentType === UserTransactionPaymentType.BANK;

  const Schema = Yup.object().shape({
    paymentImage: isBank
      ? Yup.mixed<File>().required('กรุณาแนบหลักฐานการชำระเงิน')
      : Yup.mixed<File>().nullable().notRequired(),
    paymentType: isPaymentTypeNull
      ? Yup.string().required('กรุณาเลือกช่องทางการชำระเงิน')
      : Yup.string().notRequired(),
  });

  const methods = useForm({
    mode: 'onChange',
    resolver: yupResolver(Schema),
    defaultValues,
  });

  const {
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { isSubmitting },
  } = methods;

  const selectedPaymentType = watch('paymentType');

  const beforeClose = () => {
    reset(defaultValues);
    onClose();
  };

  const onSubmit = handleSubmit(async (values) => {
    try {
      let paymentImageUrl: string | undefined;

      if (values.paymentImage) {
        paymentImageUrl = await uploadImage(values.paymentImage, 'images');
      }

      const body: { currentDateTime: string; paymentImageUrl?: string; paymentType?: string } = {
        currentDateTime,
      };

      if (paymentImageUrl) {
        body.paymentImageUrl = paymentImageUrl;
      }

      // ส่ง paymentType ถ้าเป็นกรณีที่ต้องเลือกใหม่
      if (isPaymentTypeNull && values.paymentType) {
        body.paymentType = values.paymentType;
      }

      await axios.post(endpoints.transactions.position.paymentBuyPosition(tx.id), body);

      enqueueSnackbar('อัพเดทข้อมูลสำเร็จ', { variant: 'success' });
      handleReload();
      beforeClose();
    } catch (error) {
      toastError(error);
    }
  });

  const handleConfirmCredit = async () => {
    try {
      isConfirmingCredit.onTrue();

      await axios.post(endpoints.transactions.position.paymentBuyPosition(tx.id), {
        currentDateTime,
      });

      enqueueSnackbar('ยืนยันการชำระเงินสำเร็จ', { variant: 'success' });
      handleReload();
      beforeClose();
    } catch (error) {
      toastError(error);
    } finally {
      isConfirmingCredit.onFalse();
    }
  };

  const handleDropImage = useCallback(
    (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];

      const newFile = Object.assign(file, {
        preview: URL.createObjectURL(file),
      });

      if (file) {
        setValue('paymentImage', newFile, { shouldValidate: true });
      }
    },
    [setValue]
  );

  const isCredit = tx.paymentType === UserTransactionPaymentType.CREDIT;
  const isBankOrCash =
    tx.paymentType === UserTransactionPaymentType.BANK ||
    tx.paymentType === UserTransactionPaymentType.CASH;

  // กรณี paymentType = null ให้แสดง dialog เลือก payment type
  if (isPaymentTypeNull) {
    return (
      <Dialog fullWidth maxWidth="sm" open={open} onClose={beforeClose}>
        <FormProvider methods={methods} onSubmit={onSubmit}>
          <DialogTitle>เลือกช่องทางการชำระเงิน</DialogTitle>
          <DialogContent>
            <Stack spacing={3} py={1}>
              <Typography variant="h6" color="primary">
                ยอดเงินที่ต้องชำระ: {fNumber(tx.total, 2)} บาท
              </Typography>

              <RHFSelect
                name="paymentType"
                label="ช่องทางการชำระเงิน"
                placeholder="เลือกช่องทางการชำระเงิน"
              >
                {paymentTypeOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </RHFSelect>

              {/* แสดง upload box ถ้าเลือก BANK */}
              {selectedPaymentType === UserTransactionPaymentType.BANK && (
                <RHFUploadBox
                  name="paymentImage"
                  maxSize={3145728}
                  onDrop={handleDropImage}
                  placeholder="อัพโหลดรูปหลักฐาน"
                  sx={{
                    maxHeight: 140,
                    width: '100%',
                    height: '100%',
                    aspectRatio: '1/1',
                    mx: 'auto',
                  }}
                  helperText={
                    <>
                      อนุญาตไฟล์ *.jpeg, *.jpg, *.png, *.gif
                      <br /> ขนาดสูงสุด {fData(3145728)}
                    </>
                  }
                />
              )}

              {/* แสดงข้อมูลเครดิตถ้าเลือก CREDIT */}
              {selectedPaymentType === UserTransactionPaymentType.CREDIT && (
                <Stack spacing={1}>
                  <Typography variant="body2">
                    เครดิตในกระเป๋า: {fNumber(tx.user?.userWallet?.credit || 0, 2)} บาท
                  </Typography>
                  {(tx.user?.userWallet?.credit || 0) < tx.total && (
                    <Typography variant="body2" color="error">
                      ⚠️ เครดิตไม่เพียงพอสำหรับชำระเงิน
                    </Typography>
                  )}
                </Stack>
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button variant="outlined" onClick={beforeClose} disabled={isSubmitting}>
              ยกเลิก
            </Button>
            <LoadingButton
              variant="contained"
              type="submit"
              loading={isSubmitting}
              disabled={
                selectedPaymentType === UserTransactionPaymentType.CREDIT &&
                (tx.user?.userWallet?.credit || 0) < tx.total
              }
            >
              บันทึก
            </LoadingButton>
          </DialogActions>
        </FormProvider>
      </Dialog>
    );
  }

  if (isCredit) {
    const userCredit = tx.user?.userWallet?.credit || 0;
    const hasInsufficientCredit = userCredit < tx.total;

    return (
      <Dialog fullWidth maxWidth="xs" open={open} onClose={beforeClose}>
        <DialogTitle>ยืนยันการชำระเงิน</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              ช่องทางการชำระเงิน: {paymentTypeLabel(tx.paymentType)}
            </Typography>
            <Typography variant="h6" color="primary">
              ยอดเงินที่ต้องชำระ: {fNumber(tx.total, 2)} บาท
            </Typography>
            <Typography variant="body2">เครดิตในกระเป๋า: {fNumber(userCredit, 2)} บาท</Typography>
            {hasInsufficientCredit && (
              <Typography variant="body2" color="error">
                ⚠️ เครดิตไม่เพียงพอสำหรับชำระเงิน
              </Typography>
            )}
            <Typography variant="body2" color="text.secondary">
              หมายเหตุ: กรุณาตรวจสอบยอดเงินให้ถูกต้องก่อนยืนยัน
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={beforeClose} disabled={isConfirmingCredit.value}>
            ยกเลิก
          </Button>
          <LoadingButton
            variant="contained"
            onClick={handleConfirmCredit}
            loading={isConfirmingCredit.value}
            disabled={hasInsufficientCredit || isConfirmingCredit.value}
          >
            ยืนยัน
          </LoadingButton>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog fullWidth maxWidth="sm" open={open} onClose={beforeClose}>
      <FormProvider methods={methods} onSubmit={onSubmit}>
        <DialogTitle>อัพเดทหลักฐานการชำระเงิน</DialogTitle>
        <DialogContent>
          <Stack spacing={3} py={1}>
            <Typography variant="body2" color="text.secondary">
              ช่องทางการชำระเงิน: {paymentTypeLabel(tx.paymentType)}
            </Typography>

            {isBankOrCash && (
              <RHFUploadBox
                name="paymentImage"
                maxSize={3145728}
                onDrop={handleDropImage}
                placeholder="อัพโหลดรูปหลักฐาน"
                sx={{
                  maxHeight: 140,
                  width: '100%',
                  height: '100%',
                  aspectRatio: '1/1',
                  mx: 'auto',
                }}
                helperText={
                  <>
                    อนุญาตไฟล์ *.jpeg, *.jpg, *.png, *.gif
                    <br /> ขนาดสูงสุด {fData(3145728)}
                  </>
                }
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={beforeClose} disabled={isSubmitting}>
            ยกเลิก
          </Button>
          <LoadingButton variant="contained" type="submit" loading={isSubmitting}>
            บันทึก
          </LoadingButton>
        </DialogActions>
      </FormProvider>
    </Dialog>
  );
}
